import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface OptionCardProps {
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  title: string;
  description?: string;
  hint?: string;
}

/**
 * Reusable clickable tile used across every wizard step. Large touch
 * target, clear selected-state, full-width by default — sits inside a
 * 1-col grid on phones and 2-col at sm+.
 */
export function OptionCard({ selected, onClick, icon, title, description, hint }: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "group relative flex w-full items-start gap-4 rounded-xl border p-4 text-left transition-all sm:p-5",
        "min-h-[88px]",   // touch-friendly tap area
        selected
          ? "border-brand-accent bg-brand-accentSoft ring-2 ring-brand-accentLighter shadow-brand-md"
          : "border-border bg-card hover:border-brand-borderStrong hover:shadow-brand-sm"
      )}
    >
      {icon && (
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            selected ? "bg-primary text-primary-foreground" : "bg-brand-accentSoft text-primary"
          )}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-display text-base font-semibold tracking-tight sm:text-lg">{title}</p>
        {description && (
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        )}
        {hint && (
          <p className="mt-1 text-xs font-medium uppercase tracking-wider text-brand-accentDark">
            {hint}
          </p>
        )}
      </div>
      {selected && (
        <div className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground sm:right-4 sm:top-4">
          <Check className="h-3.5 w-3.5" />
        </div>
      )}
    </button>
  );
}
